import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, ModalBody, ModalFooter } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Cover } from '../../../music24/components/primitives/Cover';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../catalogArtwork';
import { formatCatalogDay } from '../catalogDates';
import { catalogSongMessages } from './catalogSongMessages';
import { MAX_MASTER_MB } from './masterFile';
import { useMasterUpload } from './useMasterUpload';
import { MasterDropZone } from './MasterDropZone';
import { MasterProgress } from './MasterProgress';
import { PickedMasterFile } from './PickedMasterFile';
import { RightsConfirmRow } from './RightsConfirmRow';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MasterStepProps {
  accountId: number;
  /** The row the claim or the add just saved. */
  item: CatalogItem;
  /** Which step 1 came before: a claim, or a release added by hand. */
  firstStep: 'claimed' | 'added';
  onDone: () => void;
}

/**
 * Step 2 of the claim and of "Add a release" (design k1, D27): add the master of the song that is
 * now in the catalog. The row is already saved, so the claim never waits for the upload. "I'll do
 * it later" closes with no change; "Save the master" uploads and closes.
 */
const MasterStepComponent: React.FC<MasterStepProps> = ({ accountId, item, firstStep, onDone }) => {
  const intl = useIntl();
  const [file, setFile] = useState<File>();
  const [rightsChecked, setRightsChecked] = useState(false);
  const { upload, cancel, canStop, progress, error, needsRights } = useMasterUpload({
    accountId,
    itemUuid: item.uuid,
  });
  const removeFile = useCallback(() => setFile(undefined), []);
  const isUploading = progress !== null;

  const save = useCallback(() => {
    if (file === undefined) {
      return;
    }
    upload(file, rightsChecked)
      .then((outcome) => {
        if (outcome === 'saved') {
          onDone();
        }
      })
      .catch(console.error);
  }, [file, onDone, rightsChecked, upload]);

  const facts = [
    item.artistName,
    item.releaseType,
    item.releaseDate === null ? null : formatCatalogDay(intl, item.releaseDate),
    intl.formatMessage(catalogSongMessages.stepInCatalog),
  ].filter((part): part is string => part !== null && part !== '');

  return (
    <>
      <ModalBody className="d-flex flex-column gap-3">
        <div>
          <div className={styles.steps}>
            <span className={styles.stepDone}>
              {intl.formatMessage(
                firstStep === 'claimed' ? catalogSongMessages.stepClaimed : catalogSongMessages.stepAdded
              )}
            </span>
            <span>/</span>
            <span className={styles.stepOn}>{intl.formatMessage(catalogSongMessages.stepMaster)}</span>
          </div>
          <h3 className={styles.stepTitle}>
            {intl.formatMessage(catalogSongMessages.stepTitle, { title: item.title })}
          </h3>
          <p className={styles.small}>{intl.formatMessage(catalogSongMessages.stepBody)}</p>
        </div>
        <div className={styles.songOk}>
          <Cover src={catalogArtworkSrc(item, 38)} label={item.title} size={38} />
          <div className={styles.grow}>
            <span className={styles.strong}>{item.title}</span>
            <span className={styles.mono}>{facts.join(' · ')}</span>
          </div>
        </div>
        {isUploading && (
          <MasterProgress title={file?.name ?? ''} progress={progress} onStop={canStop ? cancel : undefined} />
        )}
        {!isUploading && file !== undefined && (
          <PickedMasterFile file={file} isWaitingForRights={false} onRemove={removeFile} />
        )}
        {!isUploading && file === undefined && (
          <MasterDropZone
            title={intl.formatMessage(catalogSongMessages.dropTitle)}
            hint={intl.formatMessage(catalogSongMessages.dropHint, { max: MAX_MASTER_MB })}
            onFile={setFile}
          />
        )}
        {needsRights && <RightsConfirmRow checked={rightsChecked} onChange={setRightsChecked} disabled={isUploading} />}
        {error !== null && (
          <Alert color="danger" className="mb-0">
            {withBrandWord(error)}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <p className={styles.footNote}>{intl.formatMessage(catalogSongMessages.laterNote)}</p>
        <SMLButton outline omitCap onClick={onDone} disabled={isUploading}>
          {intl.formatMessage(catalogSongMessages.later)}
        </SMLButton>
        <SMLButton
          color="primary"
          omitCap
          onClick={save}
          isLoading={isUploading}
          disabled={file === undefined || isUploading || (needsRights && !rightsChecked)}
        >
          {intl.formatMessage(catalogSongMessages.save)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

export const MasterStep = TypedMemo(MasterStepComponent);
